/**
 * Division colours for one event.
 *
 * Reads `schedule_color` from event_division_format_overrides (keyed on
 * event_id + division text). Anything without a stored colour falls back to
 * the deterministic palette hash — nothing is written back for those.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  resolveDivisionColors,
  type DivisionColors,
  type ThemeMode,
} from '@/lib/divisionPalette';

/** Tracks the `dark` class on <html> so colours follow the theme toggle. */
export function useThemeMode(): ThemeMode {
  const [mode, setMode] = useState<ThemeMode>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const read = () => setMode(root.classList.contains('dark') ? 'dark' : 'light');
    read();
    const obs = new MutationObserver(read);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return mode;
}

export interface UseDivisionColors {
  /** Theme-resolved colours for a division. Always returns something. */
  colorFor: (division: string | null | undefined) => DivisionColors;
  /** Raw stored values, division -> schedule_color (only rows that have one). */
  stored: Record<string, string>;
  mode: ThemeMode;
  loading: boolean;
  reload: () => Promise<void>;
}

export function useDivisionColors(eventId: string | null | undefined): UseDivisionColors {
  const mode = useThemeMode();
  const [stored, setStored] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!eventId) { setStored({}); return; }
    setLoading(true);
    const { data } = await (supabase as any)
      .from('event_division_format_overrides')
      .select('division, schedule_color')
      .eq('event_id', eventId);
    const map: Record<string, string> = {};
    for (const r of data ?? []) if (r?.schedule_color) map[r.division] = r.schedule_color;
    setStored(map);
    setLoading(false);
  }, [eventId]);

  useEffect(() => { void reload(); }, [reload]);

  const colorFor = useCallback(
    (division: string | null | undefined) =>
      resolveDivisionColors(division, division ? stored[division] ?? null : null, mode),
    [stored, mode],
  );

  return useMemo(() => ({ colorFor, stored, mode, loading, reload }), [colorFor, stored, mode, loading, reload]);
}
